/**
 * Tiny synth-only SFX module — no audio files needed.
 * Uses the Web Audio API directly. All sounds are short, retro-feeling blips.
 */

let ctx: AudioContext | null = null;
let muted = false;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    try {
      const Ctor: typeof AudioContext =
        // @ts-expect-error legacy webkit prefix
        window.AudioContext || window.webkitAudioContext;
      ctx = new Ctor();
    } catch {
      ctx = null;
    }
  }
  // Some browsers suspend until first interaction.
  if (ctx && ctx.state === 'suspended') {
    void ctx.resume().catch(() => undefined);
  }
  return ctx;
}

function envelope(
  c: AudioContext,
  destination: AudioNode,
  attack: number,
  decay: number,
  peak: number,
): GainNode {
  const g = c.createGain();
  const t = c.currentTime;
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.connect(destination);
  return g;
}

function tone(
  freqStart: number,
  freqEnd: number,
  duration: number,
  type: OscillatorType,
  gain = 0.18,
) {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  osc.type = type;
  const t = c.currentTime;
  osc.frequency.setValueAtTime(freqStart, t);
  osc.frequency.exponentialRampToValueAtTime(Math.max(40, freqEnd), t + duration);
  const env = envelope(c, c.destination, 0.005, duration, gain);
  osc.connect(env);
  osc.start();
  osc.stop(t + duration + 0.02);
}

function noiseBurst(duration: number, gain = 0.12, filterFreq = 1200) {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const buffer = c.createBuffer(1, Math.floor(c.sampleRate * duration), c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = filterFreq;
  const env = envelope(c, c.destination, 0.002, duration, gain);
  src.connect(filter).connect(env);
  src.start();
  src.stop(c.currentTime + duration + 0.02);
}

export const Sfx = {
  jump() {
    tone(380, 720, 0.12, 'square', 0.16);
  },
  kick() {
    tone(220, 110, 0.08, 'sawtooth', 0.18);
    noiseBurst(0.08, 0.1, 2400);
  },
  shoot() {
    // Sharp pew-pew: high freq sweep + noise click
    tone(1200, 320, 0.06, 'square', 0.13);
    noiseBurst(0.03, 0.08, 4000);
  },
  empty() {
    // Dry click - dry empty chamber
    noiseBurst(0.02, 0.06, 2200);
  },
  spikeClick() {
    // Soft low tick — warning that a jumping spike is about to extend.
    // Was 880Hz square (right in the ear-piercing band) at 80ms; lowered
    // to 380Hz triangle at 50ms so it's a wooden "tk" instead of a beep.
    tone(380, 320, 0.025, 'triangle', 0.06);
  },
  ufo() {
    // Wobbly sci-fi tone for UFO destroyed
    tone(660, 220, 0.18, 'sine', 0.16);
    setTimeout(() => tone(440, 110, 0.12, 'triangle', 0.14), 80);
  },
  bossEnter() {
    // Low rumble + falling sweep — bad guy arriving
    tone(110, 60, 0.5, 'sawtooth', 0.22);
    noiseBurst(0.4, 0.18, 600);
  },
  bossHit() {
    // Heavy thud
    tone(180, 80, 0.08, 'sawtooth', 0.16);
    noiseBurst(0.06, 0.12, 1400);
  },
  bossDie() {
    // Big triumphant explosion
    tone(220, 60, 0.6, 'sawtooth', 0.28);
    noiseBurst(0.6, 0.25, 800);
    setTimeout(() => tone(440, 880, 0.4, 'square', 0.16), 200);
  },
  victory() {
    // Triumphant fanfare
    tone(523, 523, 0.12, 'square', 0.18);
    setTimeout(() => tone(659, 659, 0.12, 'square', 0.18), 130);
    setTimeout(() => tone(784, 784, 0.12, 'square', 0.18), 260);
    setTimeout(() => tone(1047, 1047, 0.4, 'square', 0.2), 400);
  },
  hurt() {
    tone(280, 80, 0.22, 'square', 0.22);
  },
  star() {
    tone(880, 1760, 0.09, 'triangle', 0.14);
  },
  heart() {
    tone(523, 880, 0.18, 'sine', 0.18);
    setTimeout(() => tone(659, 988, 0.14, 'sine', 0.16), 60);
  },
  hit() {
    noiseBurst(0.05, 0.15, 1800);
  },
  death() {
    tone(440, 60, 0.6, 'sawtooth', 0.22);
  },
  wave() {
    tone(523, 784, 0.18, 'square', 0.14);
    setTimeout(() => tone(659, 988, 0.18, 'square', 0.14), 80);
  },
  setMuted(m: boolean) {
    muted = m;
    try {
      localStorage.setItem('astrodinos.muted', m ? '1' : '0');
    } catch {
      // ignore
    }
    // Stop background music too — sound and music share the toggle.
    void import('./Music').then(({ Music }) => Music.setMuted(m));
  },
  isMuted() {
    return muted;
  },
  loadMuteFromStorage() {
    try {
      muted = localStorage.getItem('astrodinos.muted') === '1';
    } catch {
      // ignore
    }
  },
};

Sfx.loadMuteFromStorage();
